import { customElement, property } from "lit/decorators.js";
import { LitElement, html } from "lit";
import { loadedAssets } from "@/loader";
import { AsepriteResource } from "@excaliburjs/plugin-aseprite";
import { loadResource } from "@/utils/resource";
import { extractFrameFromSpriteSheet } from "@/utils/spritesheet";

@customElement("build-mode")
export class BuildMode extends LitElement {
  @property() isOpen: boolean = false;
  @property() isLoaded: boolean = false;
  @property() furnitures: { slug: string; img?: string; amount: number }[] = [];
  @property() isCollapsed: boolean = false;

  constructor() {
    super();
  }

  protected createRenderRoot() {
    return this; // Uses light DOM
  }

  public async open() {
    try {
      // Fetch furniture data and start loading assets in parallel
      const furnitureDataPromise = this.fetchFurnitureData();

      // Wait for data, then process assets
      await furnitureDataPromise;
      const assetPromises = this.furnitures.map((furniture) =>
        loadedAssets[furniture.slug]
          ? this.updateFurnitureImage(loadedAssets[furniture.slug], furniture)
          : this.loadAsset(furniture)
      );

      // Wait for all asset loading (even if some fail)
      await Promise.allSettled(assetPromises);

      // Now open the UI smoothly
      this.isOpen = true;
    } catch (error) {
      console.error("Error loading furniture:", error);
    }
  }

  public close() {
    this.isOpen = false;
    this.requestUpdate(); // Ensures the UI updates when `isOpen` changes
  }

  private async fetchFurnitureData() {
    try {
      const response = await fetch(`${process.env.BASE_API_URL}/inventory`, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok)
        throw new Error(`Failed to fetch inventory: ${response.statusText}`);
      const data = await response.json();
      this.furnitures = data.inventory || [];
    } catch (error) {
      console.error("Error fetching furniture data:", error);
    }
  }

  private handleCancel() {
    const event = new CustomEvent("buildmode-furniture-unselect", {
      bubbles: true,
      composed: true,
    });

    this.isCollapsed = false;
    this.dispatchEvent(event);
  }

  private async loadAsset(furniture: any) {
    try {
      const resource = await loadResource(furniture.slug);
      this.updateFurnitureImage(resource, furniture);
    } catch (error) {
      console.log(`Error loading asset: ${furniture.slug}`, error);
    }
  }

  private updateFurnitureImage(
    resource: AsepriteResource,
    { slug }: { slug: string }
  ) {
    const sprite = resource.data.getSpriteSheet().sprites[0];

    if (sprite && sprite.image && sprite.image.data && sprite.sourceView) {
      const canvas = extractFrameFromSpriteSheet(
        sprite.image.data,
        sprite.sourceView
      );

      const furniture = this.furnitures.find((item) => item.slug === slug);
      if (furniture) furniture.img = canvas.toDataURL();

      this.requestUpdate();
    } else {
      console.error("Invalid sprite data or source view.");
    }
  }

  private handleFurnitureClick(slug: string, e: MouseEvent) {
    // Find the clicked furniture item
    const furniture = this.furnitures.find((item) => item.slug === slug);

    if (furniture && furniture.amount > 0) {
      // Decrease the amount when furniture is placed
      furniture.amount -= 1;

      // Dispatch event for placing the furniture
      const event = new CustomEvent("buildmode-furniture-selected", {
        detail: {
          slug,
          x: e.clientX,
          y: e.clientY,
        },
        bubbles: true,
        composed: true,
      });

      this.isCollapsed = true;
      this.dispatchEvent(event);
      this.requestUpdate(); // Update the UI to reflect the new amount
    }
  }

  collapse() {
    this.isCollapsed = false;
  }

  render() {
    if (!this.isOpen) return html``;

    return html`
      <div class="build-mode${this.isCollapsed ? " is-collapsed" : ""}">
        <button class="build-mode__close" @click="${this.close}">
          <span>Close</span>
        </button>
        ${!this.isCollapsed
          ? html`
              ${this.furnitures.length === 0
                ? html`
                    <p class="message">You have no items in your inventory.</p>
                  `
                : html`
                    <div class="cards">
                      ${this.furnitures.length > 0
                        ? this.furnitures.map(
                            (item) => html`
                              <div class="card">
                                <h3>${item.slug}</h3>
                                <div class="card-image">
                                  <img
                                    src="${item.img || ""}"
                                    alt="${item.slug}"
                                  />
                                </div>
                                <button
                                  class="btn"
                                  ?disabled="${item.amount <= 0}"
                                  @click="${(event) =>
                                    this.handleFurnitureClick(
                                      item.slug,
                                      event
                                    )}"
                                >
                                  <span class="btn__amount"
                                    >${item.amount}</span
                                  >
                                  Place
                                </button>
                              </div>
                            `
                          )
                        : ""}
                    </div>
                  `}
            `
          : html` <button
              class="btn is-cancel"
              @click="${() => this.handleCancel()}"
            >
              Cancel
            </button>`}
      </div>
    `;
  }
}
